import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { z } from 'zod';
import { useEmailVerification } from '../hooks/auth-actions.js';
import { describeError, mergeFormClasses } from './shared.js';
const emailSchema = z.string().trim().min(1, 'Enter your email.').email('Enter a valid email.');
const codeSchema = z.string().trim().min(4, 'Enter the verification code.');
/**
 * Two-step verification: requests a code by email, then submits the code to
 * confirm the address. Shown post-signup or when an admin invites a user and
 * they need to confirm ownership.
 */
export function EmailVerificationForm({ initialEmail = '', onVerified, classNames, errorMessages, }) {
    const classes = mergeFormClasses(classNames);
    const { initiate, complete } = useEmailVerification();
    const [email, setEmail] = React.useState(initialEmail);
    const [flowId, setFlowId] = React.useState(null);
    const [code, setCode] = React.useState('');
    const [validationError, setValidationError] = React.useState(null);
    const [verified, setVerified] = React.useState(false);
    const error = describeError(initiate.error ?? complete.error, errorMessages);
    const handleSendCode = async (event) => {
        event.preventDefault();
        const parsed = emailSchema.safeParse(email);
        if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Enter a valid email.');
            return;
        }
        setValidationError(null);
        try {
            const result = await initiate.run({ email: parsed.data });
            setFlowId(result.flowId);
        }
        catch {
            // Captured by hook.
        }
    };
    const handleVerify = async (event) => {
        event.preventDefault();
        if (flowId === null)
            return;
        const parsed = codeSchema.safeParse(code);
        if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Enter the code.');
            return;
        }
        setValidationError(null);
        try {
            await complete.run({ flowId, code: parsed.data });
            setVerified(true);
            onVerified?.();
        }
        catch {
            // Captured by hook.
        }
    };
    if (verified) {
        return (_jsx("div", { className: classes.form, children: _jsx("p", { className: classes.success, role: "status", children: "Email verified. You can close this page." }) }));
    }
    if (flowId === null) {
        return (_jsxs("form", { className: classes.form, onSubmit: handleSendCode, noValidate: true, children: [_jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-verify-email", children: "Email" }), _jsx("input", { id: "auth-ui-verify-email", className: classes.input, type: "email", autoComplete: "email", required: true, value: email, onChange: (event) => setEmail(event.target.value) }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null] }), error !== null ? (_jsx("p", { className: classes.error, role: "alert", children: error })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: initiate.loading, children: initiate.loading ? 'Sending…' : 'Send verification code' })] }));
    }
    return (_jsxs("form", { className: classes.form, onSubmit: handleVerify, noValidate: true, children: [_jsxs("p", { className: classes.hint, children: ["Enter the code we sent to ", email, "."] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-verify-code", children: "Verification code" }), _jsx("input", { id: "auth-ui-verify-code", className: classes.input, inputMode: "numeric", autoComplete: "one-time-code", required: true, value: code, onChange: (event) => setCode(event.target.value) }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null] }), error !== null ? (_jsx("p", { className: classes.error, role: "alert", children: error })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: complete.loading, children: complete.loading ? 'Verifying…' : 'Verify email' })] }));
}
//# sourceMappingURL=EmailVerificationForm.js.map